import React, { useState } from 'react';
import { processError, toggleDebugMode } from '../utils/errorHandler';

interface ErrorDisplayProps {
  error: any;
  onDismiss?: () => void;
}

export function ErrorDisplay({ error, onDismiss }: ErrorDisplayProps) {
  const [showDetails, setShowDetails] = useState(false);
  const processed = processError(error);

  if (!error) return null;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-red-400 mt-0.5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-red-800">{processed.userMessage}</p>

          {processed.showTechnical && processed.technicalMessage && (
            <div className="mt-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-xs text-red-600 underline hover:text-red-800 focus:outline-none"
              >
                {showDetails ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
              </button>

              {showDetails && (
                <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700 font-mono">
                  {processed.technicalMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {onDismiss && (
          <div className="ml-auto pl-3">
            <button
              onClick={onDismiss}
              className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Debug mode toggle component (only shown in development)
export function DebugModeToggle() {
  const [debugEnabled, setDebugEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('debugMode') === 'true';
    }
    return false;
  });

  const handleToggle = () => {
    const newValue = !debugEnabled;
    toggleDebugMode(newValue);
    setDebugEnabled(newValue);
    window.location.reload(); // Reload to apply changes
  };

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={handleToggle}
        className={`px-3 py-1 text-xs rounded-full shadow-lg transition-colors ${
          debugEnabled
            ? 'bg-orange-500 text-white hover:bg-orange-600'
            : 'bg-gray-500 text-white hover:bg-gray-600'
        }`}
        title={debugEnabled ? 'Debug mode ON' : 'Debug mode OFF'}
      >
        🐛 Debug: {debugEnabled ? 'ON' : 'OFF'}
      </button>
    </div>
  );
}